'use client'

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'overlay-mode'
const KEEP_OPEN_KEY = 'overlay-keep-open'
const WIDTH_KEY = 'overlay-sidebar-width'

export type OverlayMode = 'dialog' | 'sidebar'

/**
 * Preferência de exibição (modal vs sidebar) compartilhada entre Filters,
 * StudiesDialog e qualquer outro consumidor de DialogSidebar. Persiste em
 * localStorage. Lê o valor armazenado em useEffect pós-hidratação pra evitar
 * mismatch SSR.
 */
export function useOverlayMode(defaultMode: OverlayMode = 'sidebar'): [OverlayMode, (m: OverlayMode) => void] {
  const [mode, setModeState] = useState<OverlayMode>(defaultMode)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored === 'dialog' || stored === 'sidebar') {
        setModeState(stored)
      }
    } catch {
      /* localStorage indisponível — fica no default */
    }
  }, [])

  const setMode = useCallback((m: OverlayMode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, m)
    } catch { /* ignore */ }
    setModeState(m)
  }, [])

  return [mode, setMode]
}

export function useSidebarKeepOpen(): [boolean, (v: boolean) => void] {
  const [keepOpen, setKeepOpenState] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const stored = window.localStorage.getItem(KEEP_OPEN_KEY)
      if (stored === '1') {
        setKeepOpenState(true)
      }
    } catch { /* ignore */ }
  }, [])

  const setKeepOpen = useCallback((v: boolean) => {
    try {
      window.localStorage.setItem(KEEP_OPEN_KEY, v ? '1' : '0')
    } catch { /* ignore */ }
    setKeepOpenState(v)
  }, [])

  return [keepOpen, setKeepOpen]
}

export function useSidebarWidth(defaultWidth = 400): [number, (w: number) => void] {
  const [width, setWidthState] = useState(defaultWidth)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const stored = window.localStorage.getItem(WIDTH_KEY)
      if (stored) {
        const n = Number(stored)
        if (Number.isFinite(n) && n >= 280 && n <= 1200) {
          setWidthState(n)
        }
      }
    } catch { /* ignore */ }
  }, [])

  const setWidth = useCallback((w: number) => {
    const clamped = Math.max(280, Math.min(1200, w))
    try {
      window.localStorage.setItem(WIDTH_KEY, String(clamped))
    } catch { /* ignore */ }
    setWidthState(clamped)
  }, [])

  return [width, setWidth]
}
